import type {
  RtiCustomFieldToggle,
  RtiFieldLeaf,
  RtiFieldSection,
} from 'model/interfaces/rti-employee-data-fields';
import {
  humanizeRtiFieldKey,
  isCustomFieldValue,
  isRtiTwelveMonthCompensationFieldKey,
  RTI_TWELVE_MONTH_COMPENSATION_FIELD_KEYS,
  tryCoerceLooseRtiObjectLeaf,
} from 'utilities/rti-employee-data-fields';

const DEFAULT_TRUE_BOOLEAN_KEYS: readonly string[] = [
  'base_salary',
  'total_cash',
  'total_direct_comp',
];

const CUSTOM_COMP_KEYS: readonly string[] = [
  'custom_comp_field_1',
  'custom_comp_field_2',
  'custom_comp_field_3',
  'custom_comp_field_4',
  'custom_comp_field_5',
];

export function isRtiAggregatedCustomCompFieldKey(fieldKey: string): boolean {
  return CUSTOM_COMP_KEYS.includes(fieldKey);
}
export function getAggregatedCompensationFieldOrder(section: RtiFieldSection): string[] {
  return Object.keys(section);
}

function normalizeFieldValue(fieldKey: string, raw: unknown): RtiFieldLeaf | null {
  if (typeof raw === 'boolean') return raw;
  if (raw === null) {
    if (isRtiAggregatedCustomCompFieldKey(fieldKey) || isRtiTwelveMonthCompensationFieldKey(fieldKey)) {
      return { label: '', enabled: false };
    }
    return false;
  }
  if (isCustomFieldValue(raw)) {
    const o = raw as Record<string, unknown>;
    const rawLabel = typeof o.label === 'string' ? o.label : '';
    const label =
      rawLabel.length > 0
        ? rawLabel
        : isRtiAggregatedCustomCompFieldKey(fieldKey)
          ? ''
          : humanizeRtiFieldKey(fieldKey);
    const enabled = typeof o.enabled === 'boolean' ? o.enabled : false;
    return { label, enabled };
  }
  const loose = tryCoerceLooseRtiObjectLeaf(fieldKey, raw);
  if (loose) {
    return loose;
  }
  return null;
}

function normalizeSection(raw: unknown): RtiFieldSection {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: RtiFieldSection = {};
  for (const [key, val] of Object.entries(raw)) {
    const normalized = normalizeFieldValue(key, val);
    if (normalized !== null) {
      out[key] = normalized;
    }
  }
  return out;
}

function mergeDefaultBooleanKeys(section: RtiFieldSection): RtiFieldSection {
  const out: RtiFieldSection = { ...section };
  for (const k of DEFAULT_TRUE_BOOLEAN_KEYS) {
    if (!(k in out)) {
      out[k] = true;
    }
  }
  for (const k of RTI_TWELVE_MONTH_COMPENSATION_FIELD_KEYS) {
    if (!(k in out)) {
      out[k] = { label: '', enabled: false };
    }
  }
  return out;
}

function coerceTwelveMonthLeavesToToggles(section: RtiFieldSection): RtiFieldSection {
  const out: RtiFieldSection = { ...section };
  for (const k of RTI_TWELVE_MONTH_COMPENSATION_FIELD_KEYS) {
    const leaf = out[k];
    if (typeof leaf === 'boolean') {
      out[k] = { label: '', enabled: leaf };
    }
  }
  return out;
}

function ensureCustomFields(section: RtiFieldSection): RtiFieldSection {
  const out: RtiFieldSection = { ...section };
  for (const key of CUSTOM_COMP_KEYS) {
    if (!(key in out)) {
      out[key] = { label: '', enabled: false };
    } else {
      const leaf = out[key];
      if (isCustomFieldValue(leaf)) {
        const o = leaf as RtiCustomFieldToggle;
        out[key] = {
          label: typeof o.label === 'string' ? o.label : '',
          enabled: typeof o.enabled === 'boolean' ? o.enabled : false,
        };
      } else {
        out[key] = { label: '', enabled: false };
      }
    }
  }
  return out;
}

export function normalizeRtiAggregatedCompensation(raw: unknown): RtiFieldSection {
  const normalized = normalizeSection(raw);
  return ensureCustomFields(coerceTwelveMonthLeavesToToggles(mergeDefaultBooleanKeys(normalized)));
}

export function normalizeRtiCompanyWideReport(raw: unknown): Record<string, unknown> {
  const base =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? { ...(raw as Record<string, unknown>) }
      : {};
  base.compensation = normalizeRtiAggregatedCompensation(base.compensation);
  return base;
}
